import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, Subscription } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment.development';

export interface SesionReproduccion {
  id_sesion_reproduccion: string;
  contenido_tipo: 'video' | 'clase' | 'leccion';
  contenido_id: number;
  fecha_inicio: string;
  ultimo_heartbeat: string;
}

export interface IniciarReproduccionRequest {
  contenido_tipo: 'video' | 'clase' | 'leccion';
  contenido_id: number;
}

export interface IniciarReproduccionResponse {
  ok: boolean;
  msg: string;
  code?: string;
  id_sesion_reproduccion?: string;
  sesion?: SesionReproduccion;
  sesiones_activas?: number;
  max_sesiones?: number;
  limite?: number;
}

export interface HeartbeatRequest {
  id_sesion_reproduccion: string;
}

export interface HeartbeatResponse {
  ok: boolean;
  msg: string;
}

export interface FinalizarReproduccionRequest {
  id_sesion_reproduccion: string;
}

export interface FinalizarReproduccionResponse {
  ok: boolean;
  msg: string;
}

export interface DispositivoActual {
  id_dispositivo: number;
  nombre_dispositivo: string;
  fingerprint: string;
  ultima_actividad: string;
  bloqueado: boolean;
}

export interface DispositivoActualResponse {
  ok: boolean;
  msg: string;
  dispositivo?: DispositivoActual;
}

export interface RegistrarDispositivoRequest {
  nombre_dispositivo: string;
}

export interface RegistrarDispositivoResponse {
  ok: boolean;
  msg: string;
  dispositivo?: DispositivoActual;
}

export interface SesionActiva {
  id_sesion_reproduccion: string;
  dispositivo: string;
  contenido_tipo: string;
  contenido_id: number;
  fecha_inicio: string;
  ultimo_heartbeat: string;
  tiempo_activo: string;
}

export interface SesionesActivasResponse {
  ok: boolean;
  msg: string;
  sesiones?: SesionActiva[];
  total_sesiones?: number;
  max_sesiones?: number;
}

export interface DispositivoListado {
  id_dispositivo: number;
  nombre_dispositivo: string;
  tipo_dispositivo: string;
  ip_dispositivo: string;
  activo: boolean;
  fecha_registro: string;
  ultimo_acceso: string;
  es_dispositivo_actual: boolean;
}

export interface DispositivosListadoResponse {
  ok: boolean;
  msg?: string;
  dispositivos?: DispositivoListado[];
  total?: number;
  activos?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReproduccionService {
  private readonly API_URL = environment.apiUrl;
  private http = inject(HttpClient);
  
  // Estado de la sesión actual
  private sesionActual: string | null = null;
  private heartbeatSubscription: Subscription | null = null;
  private readonly HEARTBEAT_INTERVAL = 90000; // 90 segundos (1.5 minutos)

  /**
   * Verifica el dispositivo actual
   */
  getDispositivoActual(): Observable<DispositivoActualResponse> {
    return this.http.get<DispositivoActualResponse>(`${this.API_URL}/dispositivos/actual`).pipe(
      tap(response => {
        console.log('📱 Dispositivo actual:', response);
      }),
      catchError(error => {
        console.error('Error al obtener dispositivo actual:', error);
        throw error;
      })
    );
  }

  /**
   * Registra un nuevo dispositivo
   */
  registrarDispositivo(nombreDispositivo: string): Observable<RegistrarDispositivoResponse> {
    return this.http.post<RegistrarDispositivoResponse>(
      `${this.API_URL}/dispositivos/registrar`,
      { nombre_dispositivo: nombreDispositivo }
    ).pipe(
      tap(response => {
        console.log('✅ Dispositivo registrado:', response);
      }),
      catchError(error => {
        console.error('Error al registrar dispositivo:', error);
        throw error;
      })
    );
  }

  /**
   * Inicia una sesión de reproducción
   */
  iniciarReproduccion(contenidoTipo: 'video' | 'clase' | 'leccion', contenidoId: number): Observable<IniciarReproduccionResponse> {
    const request: IniciarReproduccionRequest = {
      contenido_tipo: contenidoTipo,
      contenido_id: contenidoId
    };

    return this.http.post<IniciarReproduccionResponse>(
      `${this.API_URL}/reproduccion/iniciar`,
      request
    ).pipe(
      tap(response => {
        if (response.ok && response.id_sesion_reproduccion) {
          this.sesionActual = response.id_sesion_reproduccion;
          this.iniciarHeartbeat(response.id_sesion_reproduccion);
          console.log('▶️ Reproducción iniciada:', response);
          console.log(`📊 Sesiones activas: ${response.sesiones_activas}/${response.max_sesiones}`);
        }
      }),
      catchError(error => {
        if (error.status === 0) {
          console.warn('⚠️ API no disponible al iniciar reproducción');
        } else {
          console.error('❌ Error al iniciar reproducción:', error);
        }
        throw error;
      })
    );
  }

  /**
   * Envía heartbeat para mantener la sesión activa
   */
  enviarHeartbeat(idSesion: string): Observable<HeartbeatResponse> {
    const request: HeartbeatRequest = {
      id_sesion_reproduccion: idSesion
    };

    return this.http.post<HeartbeatResponse>(
      `${this.API_URL}/reproduccion/heartbeat`,
      request
    ).pipe(
      tap(response => {
        if (response.ok) {
          console.log('💓 Heartbeat enviado correctamente');
        }
      }),
      catchError(error => {
        console.error('❌ Error en heartbeat:', error);
        throw error;
      })
    );
  }

  /**
   * Finaliza una sesión de reproducción
   */
  finalizarReproduccion(idSesion?: string): Observable<FinalizarReproduccionResponse> {
    const sesionId = idSesion || this.sesionActual;
    
    if (!sesionId) {
      console.warn('⚠️ No hay sesión activa para finalizar');
      return new Observable(observer => {
        observer.next({ ok: false, msg: 'No hay sesión activa' });
        observer.complete();
      });
    }

    const request: FinalizarReproduccionRequest = {
      id_sesion_reproduccion: sesionId
    };

    return this.http.post<FinalizarReproduccionResponse>(
      `${this.API_URL}/reproduccion/finalizar`,
      request
    ).pipe(
      tap(response => {
        if (response.ok) {
          console.log('⏹️ Reproducción finalizada correctamente');
          this.detenerHeartbeat();
          this.sesionActual = null;
        }
      }),
      catchError(error => {
        console.error('❌ Error al finalizar reproducción:', error);
        this.detenerHeartbeat();
        this.sesionActual = null;
        throw error;
      })
    );
  }

  /**
   * Obtiene las sesiones activas del usuario
   */
  getSesionesActivas(): Observable<SesionesActivasResponse> {
    return this.http.get<SesionesActivasResponse>(`${this.API_URL}/dispositivos/sesiones-activas`).pipe(
      tap(response => {
        console.log('📊 Sesiones activas:', response);
      }),
      catchError(error => {
        if (error.status === 0) {
          console.warn('⚠️ API no disponible al obtener sesiones activas');
        } else {
          console.error('Error al obtener sesiones activas:', error);
        }
        throw error;
      })
    );
  }

  /**
   * Obtiene la lista completa de dispositivos del usuario
   */
  listarDispositivos(): Observable<DispositivosListadoResponse> {
    return this.http.get<DispositivosListadoResponse>(`${this.API_URL}/dispositivos/listar`).pipe(
      tap(response => {
        console.log('📱 Lista de dispositivos:', response);
        if (response.ok && response.dispositivos) {
          console.log(`Total dispositivos: ${response.total} | Activos: ${response.activos}`);
          const inactivos = response.dispositivos.filter(d => !d.activo).length;
          if (inactivos > 0) {
            console.warn(`⚠️ Dispositivos inactivos: ${inactivos}`);
          }
        }
      }),
      catchError(error => {
        console.error('Error al listar dispositivos:', error);
        throw error;
      })
    );
  }

  /**
   * Inicia el envío automático de heartbeats
   */
  private iniciarHeartbeat(idSesion: string): void {
    // Detener heartbeat anterior si existe
    this.detenerHeartbeat();

    // Crear nueva suscripción de heartbeat
    this.heartbeatSubscription = interval(this.HEARTBEAT_INTERVAL).subscribe(() => {
      this.enviarHeartbeat(idSesion).subscribe({
        next: (response) => {
          if (!response.ok) {
            console.warn('⚠️ Heartbeat falló, deteniendo...');
            this.detenerHeartbeat();
          }
        },
        error: (error) => {
          console.error('❌ Error en heartbeat automático:', error);
          this.detenerHeartbeat();
        }
      });
    });

    console.log(`💓 Heartbeat iniciado (cada ${this.HEARTBEAT_INTERVAL / 1000} segundos)`);
  }

  /**
   * Detiene el envío automático de heartbeats
   */
  private detenerHeartbeat(): void {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
      console.log('💓 Heartbeat detenido');
    }
  }

  /**
   * Obtiene el ID de la sesión actual
   */
  getSesionActual(): string | null {
    return this.sesionActual;
  }

  /**
   * Verifica si hay una sesión activa
   */
  tieneSesionActiva(): boolean {
    return this.sesionActual !== null;
  }

  /**
   * Limpia todo el estado del servicio
   */
  limpiarEstado(): void {
    this.detenerHeartbeat();
    this.sesionActual = null;
  }
}

